import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { createDemoState } from '../data/demo'
import { emptyAcademyState, loadRemoteAcademyState, remoteAcademy } from '../lib/academyApi'
import { isDemoMode } from '../lib/backend'
import { courseLessonIds, safeId } from '../lib/utils'
import type { AcademyState, CommunityPost, Course, Lesson, Module, QuizQuestion, UserStatus } from '../types'
import { useAuth } from './AuthContext'

interface AcademyContextValue {
  state: AcademyState
  loading: boolean
  error: string | null
  clearError: () => void
  refresh: () => Promise<void>
  completeLesson: (lessonId: string) => Promise<void>
  submitQuiz: (courseId: string, answers: Record<string, number>) => Promise<number>
  createPost: (category: string, title: string, body: string) => Promise<void>
  editPost: (postId: string, title: string, body: string) => Promise<void>
  deletePost: (postId: string) => Promise<void>
  reactToPost: (postId: string) => Promise<void>
  commentOnPost: (postId: string, body: string) => Promise<void>
  reportPost: (postId: string) => Promise<void>
  moderatePost: (postId: string, status: CommunityPost['status']) => Promise<void>
  updateUserStatus: (userId: string, status: UserStatus) => Promise<void>
  toggleEnrollment: (userId: string, courseId: string) => Promise<void>
  createCourse: (course: Pick<Course, 'title' | 'description'>) => Promise<void>
  updateCourse: (courseId: string, title: string, description: string, priceClp: number, salesEnabled: boolean) => Promise<void>
  setCourseStatus: (courseId: string, status: Course['status']) => Promise<void>
  archiveCourse: (courseId: string) => Promise<void>
  addModule: (courseId: string, title: string) => Promise<void>
  addLesson: (courseId: string, moduleId: string, title: string) => Promise<void>
  updateLesson: (courseId: string, moduleId: string, lessonId: string, values: Pick<Lesson, 'title' | 'summary' | 'duration' | 'content'> & { videoUrl: string }) => Promise<void>
  uploadLessonFile: (lessonId: string, kind: 'video' | 'resource', file: File, title?: string) => Promise<void>
  moveModule: (courseId: string, moduleId: string, direction: -1 | 1) => Promise<void>
  moveLesson: (courseId: string, moduleId: string, lessonId: string, direction: -1 | 1) => Promise<void>
  createQuiz: (courseId: string, title: string) => Promise<void>
  addQuizQuestion: (quizId: string, question: Omit<QuizQuestion, 'id'>) => Promise<void>
  publishAnnouncement: (title: string, body: string) => Promise<void>
  updateProfile: (values: { name: string; country: string; profession: string; interests: string[] }) => Promise<void>
  resetDemo: () => void
}

const storageKey = 'academia-demo-data-v1'
const AcademyContext = createContext<AcademyContextValue | null>(null)
const legacyCourseAccents = new Set(['#6b4035', '#8a5848', '#a0714f', '#293d35', '#4a5058', '#484a49'])

const migrateCoursePalette = (state: AcademyState): AcademyState => ({
  ...state,
  courses: state.courses.map((course) => legacyCourseAccents.has(course.accent.toLowerCase()) ? { ...course, accent: '#c66e56' } : course),
})

const demoInitialState = () => {
  try {
    const saved = localStorage.getItem(storageKey)
    if (!saved) return createDemoState()
    const migrated = migrateCoursePalette(JSON.parse(saved) as AcademyState)
    localStorage.setItem(storageKey, JSON.stringify(migrated))
    return migrated
  } catch {
    return createDemoState()
  }
}

const slugify = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

export function AcademyProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [state, setState] = useState<AcademyState>(() => isDemoMode ? demoInitialState() : emptyAcademyState())
  const [loading, setLoading] = useState(!isDemoMode)
  const [error, setError] = useState<string | null>(null)

  const updateDemo = useCallback((recipe: (current: AcademyState) => AcademyState, audit?: { action: string; targetType: string; targetId: string }) => {
    setState((current) => {
      let next = recipe(current)
      if (audit && user) next = { ...next, audit: [{ id: safeId('audit'), actorId: user.id, at: new Date().toISOString(), ...audit }, ...next.audit] }
      localStorage.setItem(storageKey, JSON.stringify(next))
      return next
    })
  }, [user])

  const refresh = useCallback(async () => {
    if (isDemoMode) return
    setLoading(true)
    try {
      setState(await loadRemoteAcademyState())
      setError(null)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'No fue posible actualizar la academia.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const timer = window.setTimeout(() => { void refresh() }, 0)
    return () => window.clearTimeout(timer)
  }, [refresh, user?.id])

  const runRemote = useCallback(async <T,>(operation: () => Promise<T>, reload = true): Promise<T> => {
    setError(null)
    try {
      const result = await operation()
      if (reload) await refresh()
      return result
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : 'No fue posible completar la operación.'
      setError(message)
      throw new Error(message, { cause })
    }
  }, [refresh])

  const value = useMemo<AcademyContextValue>(() => ({
    state,
    loading,
    error,
    clearError: () => setError(null),
    refresh,
    completeLesson: async (lessonId) => {
      if (!user) return
      if (!isDemoMode) return runRemote(() => remoteAcademy.completeLesson(user.id, lessonId))
      updateDemo((current) => {
        const completed = current.completedLessons[user.id] ?? []
        if (completed.includes(lessonId)) return current
        return { ...current, completedLessons: { ...current.completedLessons, [user.id]: [...completed, lessonId] } }
      })
    },
    submitQuiz: async (courseId, answers) => {
      if (!user) throw new Error('Debes iniciar sesión para enviar el cuestionario.')
      const quiz = state.quizzes.find((item) => item.courseId === courseId)
      if (!quiz) throw new Error('No se encontró el cuestionario.')
      if (!isDemoMode) return runRemote(() => remoteAcademy.submitQuiz(quiz.id, answers, courseId))
      const correct = quiz.questions.filter((question) => answers[question.id] === question.correctIndex).length
      const score = Math.round((correct / quiz.questions.length) * 100)
      updateDemo((current) => {
        const course = current.courses.find((item) => item.id === courseId)
        const completed = current.completedLessons[user.id] ?? []
        const eligible = course && score >= quiz.passingScore && courseLessonIds(course).every((id) => completed.includes(id))
        const exists = current.certificates.some((item) => item.userId === user.id && item.courseId === courseId)
        return {
          ...current,
          quizScores: { ...current.quizScores, [`${user.id}:${courseId}`]: score },
          certificates: eligible && !exists ? [...current.certificates, { id: safeId('certificate'), userId: user.id, courseId, issuedAt: new Date().toISOString(), verificationCode: `MIC-DEMO-${crypto.randomUUID().slice(0, 8).toUpperCase()}` }] : current.certificates,
        }
      })
      return score
    },
    createPost: async (category, title, body) => {
      if (!user) return
      if (!isDemoMode) return runRemote(() => remoteAcademy.createPost(user.id, category, title, body))
      const post: CommunityPost = { id: safeId('post'), category, title, body, authorId: user.id, authorName: user.name, createdAt: new Date().toISOString(), reactions: [], comments: [], status: 'visible' }
      updateDemo((current) => ({ ...current, posts: [post, ...current.posts] }))
    },
    editPost: async (postId, title, body) => {
      if (!isDemoMode) return runRemote(() => remoteAcademy.editPost(postId, title, body))
      updateDemo((current) => ({ ...current, posts: current.posts.map((post) => post.id === postId && (post.authorId === user?.id || user?.role === 'admin') ? { ...post, title, body } : post) }))
    },
    deletePost: async (postId) => {
      if (!isDemoMode) return runRemote(() => remoteAcademy.deletePost(postId))
      updateDemo((current) => ({ ...current, posts: current.posts.filter((post) => post.id !== postId || (post.authorId !== user?.id && user?.role !== 'admin')) }))
    },
    reactToPost: async (postId) => {
      if (!user) return
      if (!isDemoMode) return runRemote(() => remoteAcademy.reactToPost(user.id, postId))
      updateDemo((current) => ({ ...current, posts: current.posts.map((post) => post.id === postId ? { ...post, reactions: post.reactions.includes(user.id) ? post.reactions.filter((id) => id !== user.id) : [...post.reactions, user.id] } : post) }))
    },
    commentOnPost: async (postId, body) => {
      if (!user) return
      if (!isDemoMode) return runRemote(() => remoteAcademy.commentOnPost(user.id, postId, body))
      updateDemo((current) => ({ ...current, posts: current.posts.map((post) => post.id === postId ? { ...post, comments: [...post.comments, { id: safeId('comment'), authorId: user.id, authorName: user.name, body, createdAt: new Date().toISOString() }] } : post) }))
    },
    reportPost: async (postId) => {
      if (!user) return
      if (!isDemoMode) return runRemote(() => remoteAcademy.reportPost(user.id, postId))
      updateDemo((current) => ({ ...current, posts: current.posts.map((post) => post.id === postId ? { ...post, status: 'reported' } : post) }), { action: 'community.report', targetType: 'post', targetId: postId })
    },
    moderatePost: async (postId, status) => {
      if (!isDemoMode) return runRemote(() => remoteAcademy.moderatePost(postId, status))
      updateDemo((current) => ({ ...current, posts: current.posts.map((post) => post.id === postId ? { ...post, status } : post) }), { action: 'community.moderate', targetType: 'post', targetId: postId })
    },
    updateUserStatus: async (userId, status) => {
      if (!isDemoMode) return runRemote(() => remoteAcademy.updateUserStatus(userId, status))
      updateDemo((current) => ({ ...current, users: current.users.map((profile) => profile.id === userId ? { ...profile, status } : profile) }), { action: 'user.status.update', targetType: 'profile', targetId: userId })
    },
    toggleEnrollment: async (userId, courseId) => {
      if (!isDemoMode) return runRemote(() => remoteAcademy.toggleEnrollment(userId, courseId))
      updateDemo((current) => {
        const existing = current.enrollments.find((item) => item.userId === userId && item.courseId === courseId)
        if (existing) return { ...current, enrollments: current.enrollments.map((item) => item.id === existing.id ? { ...item, active: !item.active } : item) }
        return { ...current, enrollments: [...current.enrollments, { id: safeId('enrollment'), userId, courseId, enabledAt: new Date().toISOString(), active: true }] }
      }, { action: 'enrollment.toggle', targetType: 'profile', targetId: userId })
    },
    createCourse: async ({ title, description }) => {
      const slug = slugify(title)
      if (!isDemoMode) return runRemote(() => remoteAcademy.createCourse({ title, description }, slug))
      const course: Course = { id: safeId('course'), slug, title, description, longDescription: description, eyebrow: 'Curso en preparación', level: 'Inicial', duration: 'Por definir', status: 'draft', accent: '#c66e56', priceClp: 0, currency: 'CLP', salesEnabled: false, checkoutAvailable: false, modules: [] }
      updateDemo((current) => ({ ...current, courses: [...current.courses, course] }), { action: 'course.create', targetType: 'course', targetId: course.id })
    },
    updateCourse: async (courseId, title, description, priceClp, salesEnabled) => {
      if (!isDemoMode) return runRemote(() => remoteAcademy.updateCourse(courseId, title, description, priceClp, salesEnabled))
      updateDemo((current) => ({ ...current, courses: current.courses.map((course) => course.id === courseId ? { ...course, title, description, longDescription: description, priceClp, salesEnabled } : course) }), { action: 'course.update', targetType: 'course', targetId: courseId })
    },
    setCourseStatus: async (courseId, status) => {
      if (!isDemoMode) return runRemote(() => remoteAcademy.setCourseStatus(courseId, status))
      updateDemo((current) => ({ ...current, courses: current.courses.map((course) => course.id === courseId ? { ...course, status } : course) }), { action: 'course.status.update', targetType: 'course', targetId: courseId })
    },
    archiveCourse: async (courseId) => {
      const current = state.courses.find((course) => course.id === courseId)
      const status = current?.status === 'archived' ? 'draft' : 'archived'
      if (!isDemoMode) return runRemote(() => remoteAcademy.setCourseStatus(courseId, status))
      updateDemo((value) => ({ ...value, courses: value.courses.map((course) => course.id === courseId ? { ...course, status } : course) }), { action: 'course.archive.toggle', targetType: 'course', targetId: courseId })
    },
    addModule: async (courseId, title) => {
      const course = state.courses.find((item) => item.id === courseId)
      if (!isDemoMode) return runRemote(() => remoteAcademy.addModule(courseId, title))
      const module: Module = { id: safeId('module'), title, description: 'Descripción pendiente.', order: (course?.modules.length ?? 0) + 1, lessons: [] }
      updateDemo((current) => ({ ...current, courses: current.courses.map((item) => item.id === courseId ? { ...item, modules: [...item.modules, module] } : item) }), { action: 'module.create', targetType: 'course', targetId: courseId })
    },
    addLesson: async (courseId, moduleId, title) => {
      const module = state.courses.find((item) => item.id === courseId)?.modules.find((item) => item.id === moduleId)
      if (!isDemoMode) return runRemote(() => remoteAcademy.addLesson(moduleId, title))
      const lesson: Lesson = { id: safeId('lesson'), title, summary: 'Contenido pendiente.', duration: '10 min', content: '<p>Contenido editable pendiente.</p>', order: (module?.lessons.length ?? 0) + 1, resourceIds: [] }
      updateDemo((current) => ({ ...current, courses: current.courses.map((course) => course.id === courseId ? { ...course, modules: course.modules.map((item) => item.id === moduleId ? { ...item, lessons: [...item.lessons, lesson] } : item) } : course) }), { action: 'lesson.create', targetType: 'module', targetId: moduleId })
    },
    updateLesson: async (courseId, moduleId, lessonId, values) => {
      if (!isDemoMode) return runRemote(() => remoteAcademy.updateLesson(lessonId, values))
      updateDemo((current) => ({ ...current, courses: current.courses.map((course) => course.id === courseId ? { ...course, modules: course.modules.map((module) => module.id === moduleId ? { ...module, lessons: module.lessons.map((lesson) => lesson.id === lessonId ? { ...lesson, ...values } : lesson) } : module) } : course) }), { action: 'lesson.update', targetType: 'lesson', targetId: lessonId })
    },
    uploadLessonFile: async (lessonId, kind, file, title) => {
      if (isDemoMode) throw new Error('La carga de archivos está disponible en la academia publicada.')
      return runRemote(() => remoteAcademy.uploadLessonFile(lessonId, kind, file, title))
    },
    moveModule: async (courseId, moduleId, direction) => {
      if (!isDemoMode) return runRemote(() => remoteAcademy.moveModule(moduleId, direction))
      updateDemo((current) => ({ ...current, courses: current.courses.map((course) => {
        if (course.id !== courseId) return course
        const modules = [...course.modules]
        const index = modules.findIndex((module) => module.id === moduleId)
        const target = index + direction
        if (index < 0 || target < 0 || target >= modules.length) return course
        ;[modules[index], modules[target]] = [modules[target], modules[index]]
        return { ...course, modules: modules.map((module, position) => ({ ...module, order: position + 1 })) }
      }) }), { action: 'module.reorder', targetType: 'course', targetId: courseId })
    },
    moveLesson: async (courseId, moduleId, lessonId, direction) => {
      if (!isDemoMode) return runRemote(() => remoteAcademy.moveLesson(lessonId, direction))
      updateDemo((current) => ({ ...current, courses: current.courses.map((course) => {
        if (course.id !== courseId) return course
        return { ...course, modules: course.modules.map((module) => {
          if (module.id !== moduleId) return module
          const lessons = [...module.lessons]
          const index = lessons.findIndex((lesson) => lesson.id === lessonId)
          const target = index + direction
          if (index < 0 || target < 0 || target >= lessons.length) return module
          ;[lessons[index], lessons[target]] = [lessons[target], lessons[index]]
          return { ...module, lessons: lessons.map((lesson, position) => ({ ...lesson, order: position + 1 })) }
        }) }
      }) }), { action: 'lesson.reorder', targetType: 'module', targetId: moduleId })
    },
    createQuiz: async (courseId, title) => {
      if (!isDemoMode) return runRemote(() => remoteAcademy.createQuiz(courseId, title))
      const quizId = safeId('quiz')
      updateDemo((current) => ({ ...current, courses: current.courses.map((course) => course.id === courseId ? { ...course, quizId } : course), quizzes: [...current.quizzes, { id: quizId, courseId, title, passingScore: 70, questions: [] }] }), { action: 'quiz.create', targetType: 'course', targetId: courseId })
    },
    addQuizQuestion: async (quizId, question) => {
      if (!isDemoMode) return runRemote(() => remoteAcademy.addQuizQuestion(quizId, question))
      updateDemo((current) => ({ ...current, quizzes: current.quizzes.map((item) => item.id === quizId ? { ...item, questions: [...item.questions, { id: safeId('question'), ...question }] } : item) }), { action: 'quiz.question.create', targetType: 'quiz', targetId: quizId })
    },
    publishAnnouncement: async (title, body) => {
      if (!user) return
      if (!isDemoMode) return runRemote(() => remoteAcademy.publishAnnouncement(user.id, title, body))
      updateDemo((current) => ({ ...current, announcements: [{ id: safeId('announcement'), title, body, date: new Date().toISOString(), audience: 'all' }, ...current.announcements] }), { action: 'announcement.create', targetType: 'announcement', targetId: title })
    },
    updateProfile: async (values) => {
      if (!user) return
      if (!isDemoMode) return runRemote(() => remoteAcademy.updateProfile(user.id, values))
      updateDemo((current) => ({ ...current, users: current.users.map((profile) => profile.id === user.id ? { ...profile, ...values } : profile) }))
    },
    resetDemo: () => {
      if (!isDemoMode) return
      const next = createDemoState()
      localStorage.setItem(storageKey, JSON.stringify(next))
      setState(next)
    },
  }), [error, loading, refresh, runRemote, state, updateDemo, user])

  return <AcademyContext.Provider value={value}>{children}</AcademyContext.Provider>
}

export const useAcademy = () => {
  const context = useContext(AcademyContext)
  if (!context) throw new Error('useAcademy debe usarse dentro de AcademyProvider')
  return context
}
